import { Button, Grid2 } from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
    Grid_Gap,
    Grid_Gap_LG,
    Input_Width,
    Input_Width_LG,
} from "../../../../shared/constants";
import { TextFormField } from "../../../../shared/components/formfields/TextFormField";
import { DateFormField } from "../../../../shared/components/formfields/DataFormField";
import CustomDivider from "../../../../shared/utils/CustomDivider";
import CustomSlider from "./../../../../shared/components/formfields/CustomSlider";
import { DropzoneUploadFormField } from "../../../../shared/components/formfields/DropzoneUploadFormField";
import { IdAutoCompleteField } from "../../../../shared/components/formfields/IdAutoCompleteField";
import { useToaster } from "../../../../shared/utils/ToasterProvider";
import { useMutation } from "@tanstack/react-query";
import useAuthStore from "../../../../store/useAuthStore";
import { nodeApi } from "../../../../assets/api/useStudentPostApi";

const RecurringCourses = () => {
    const { token } = useAuthStore();
    const { showToaster } = useToaster();

    const CourseCategory = [
        { id: 1, value: "category1", label: "category1" },
        { id: 2, value: "category2", label: "category2" },
    ];

    const mutation = useMutation({
        mutationFn: async (formData) => {
            const response = await nodeApi.post("events", formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        },
    });

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        const formData = new FormData();

        const topLevelFields = [
            "title",
            "category_id",
            "location_id",
            "location_address",
            "short_description",
            "description",
            "learningoutcome",
            "no_of_teachers",
            "no_of_live_class",
            "start_date",
            "end_date",
            "seats",
            "price",
            "verification_details",
        ];

        topLevelFields.forEach((field) => {
            const value = values[field];
            if (value !== undefined && value !== null) {
                formData.append(field, String(value));
            }
        });

        // Append type
        formData.append("type", 4);

        // Age range
        if (values.age?.length === 2) {
            formData.append("age_min", String(values.age[0]));
            formData.append("age_max", String(values.age[1]));
        }

        // Cover image
        if (values.cover_image instanceof File) {
            formData.append("cover_image", values.cover_image);
        } else {
            formData.append("cover_image", values.cover_image);
        }

       mutation.mutate(formData, {
            onSuccess: () => {
                showToaster("success", "Course Added Successfully", "success");
                resetForm();
                setSubmitting(false);
            },
            onError: (err) => {
                console.error("Error submitting course:", err);
                showToaster("Error occurred while submitting course.", "error", "error");
                setSubmitting(false);
            },
        });
    };

    const initialValues = {
        category_id: 0,
        location_id: 0,
        location_address: "",
        title: "",
        learningoutcome: "",
        short_description: "",
        description: "",
        age: [10, 16],
        price: 0,
        verification_details: "NA",
        cover_image: "",
        start_date: "",
        end_date: "",
        no_of_teachers: "",
        no_of_live_class: "",
        seats: "",
    };

    const validationSchema = Yup.object().shape({
        category_id: Yup.number().required("Category is required"),
        location_id: Yup.number().required("Location is required"),
        location_address: Yup.string().required("Location address is required"),
        title: Yup.string().required("Course name is required"),
        learningoutcome: Yup.string().required("Learning outcome is required"),
        short_description: Yup.string().required("Short description is required"),
        description: Yup.string().required("Description is required"),
        age: Yup.array()
            .of(Yup.number().required())
            .min(1)
            .max(20)
            .required("Age range is required"),
        price: Yup.number().required("Price is required"),
        cover_image: Yup.mixed().required("Cover image is required"),
        start_date: Yup.string().required("Start date is required"),
        end_date: Yup.string().required("End date is required"),
        no_of_teachers: Yup.string().required("No of teachers is required"),
        no_of_live_class: Yup.string().required("No of live classes is required"),
        seats: Yup.string().required("Seats are required"),
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ resetForm }) => (
                <Form>
                    <Grid2 container spacing={Grid_Gap_LG}>
                        <Grid2 children size={12}>
                            <Field
                                name="cover_image"
                                component={DropzoneUploadFormField}
                                label="Image"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="organizer_id"
                                component={IdAutoCompleteField}
                                options={CourseCategory}
                                label="Organizer"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="category_id"
                                component={IdAutoCompleteField}
                                options={CourseCategory}
                                label="Courses Category"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="location_id"
                                component={IdAutoCompleteField}
                                options={CourseCategory}
                                label="Location Id"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="location_address"
                                component={TextFormField}
                                label="Location Address"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="title"
                                component={TextFormField}
                                label="Course Name"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="short_description"
                                component={TextFormField}
                                label="Short Description"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="description"
                                component={TextFormField}
                                label="Description"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="learningoutcome"
                                component={TextFormField}
                                label="Learning Outcome"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="no_of_teachers"
                                component={TextFormField}
                                label="No Of Teachers"
                                isRequired
                                type={"number"}
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="no_of_live_class"
                                component={TextFormField}
                                label="No of Live Classes"
                                isRequired
                                type={"number"}
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="start_date"
                                component={DateFormField}
                                label="Start Date"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="end_date"
                                component={DateFormField}
                                label="End Date"
                                isRequired
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="seats"
                                component={TextFormField}
                                label="Seats"
                                type="number"
                                isRequired

                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="age"
                                component={CustomSlider}
                                label="Age"
                                initialValue={1}
                                finalValue={20}
                            />
                        </Grid2>

                        <Grid2 children size={Input_Width_LG}>
                            <Field
                                name="price"
                                component={TextFormField}
                                label="Price"
                                type="number"
                                isRequired

                            />
                        </Grid2>

                        <CustomDivider size={5} />
                    </Grid2>

                    <CustomDivider />
                    <Grid2 container spacing={Grid_Gap}>
                        <Grid2 size={Input_Width} />
                        <Grid2 size={Input_Width}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => resetForm()}
                            >
                                Cancel
                            </Button>
                        </Grid2>
                        <Grid2 size={Input_Width}>
                            <Button fullWidth variant="contained" type="submit">
                                Submit
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
            )}
        </Formik>
    );
};

export default RecurringCourses;

